import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import sha from 'sha.js';
import { useLocation } from 'react-router-dom';
import { Button, Clickable, ControlLink, Input, Stack, Text } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import isbot from 'isbot';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { Link } from '../Link';
import { ModalOverlay } from '../Modal';
import { useEmailSubscribe } from '../../hooks/useEmailSubscribe';
import { hrefFor } from '../../routers';
import { usePreferences } from '../../hooks/usePreferences';
import { useUser } from '../../hooks/useUser';
import { tc } from '../../lib/trackingContext';
import { usePageType } from '../../hooks/usePageType';
import { useCookies } from '../../hooks/useCookies';

import './EmailSignup.scss';

const PAGES_TO_VIEW_BEFORE_MODAL_SHOWS = 4;

export const EmailSignup: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const { showEmailSignup, setPreference } = usePreferences();
  const history = useHistory();
  const { user } = useUser();
  const { pathname } = useLocation();
  const {
    cookies: { cloudfrontViewerCountry }
  } = useCookies();
  const userid = user.id;

  const showPhoneNumberInput = useFeatureFlag('allow-phone-number', [
    { default: false },
    { if: true, then: true }
  ]);

  const {
    subscribe,
    isSubscribed,
    isExistingSubscription,
    error: mutationError,
    mailValidationError
  } = useEmailSubscribe();

  const error = mutationError || mailValidationError;
  const pagetype = usePageType();

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      subscribe(email, 'welcomePopup');
      tc.track('Welcome Email Modal Sign Up Finished', {
        addToTrackingEvent: {
          pagetype,
          label: userid
        }
      });
    },
    [email, subscribe, pagetype, userid]
  );

  const trackModalDismissed = useCallback(() => {
    tc.track('Welcome Email Modal Dismissed', {
      addToTrackingEvent: {
        pagetype,
        label: userid
      }
    });
  }, [pagetype, userid]);

  useEffect(() => {
    if (user.isLoggedIn && showEmailSignup) setPreference('showEmailSignup', false);
  }, [user.isLoggedIn, showEmailSignup, setPreference]);

  const showModal =
    history.length >= PAGES_TO_VIEW_BEFORE_MODAL_SHOWS &&
    showEmailSignup &&
    !user.isLoggedIn &&
    pathname !== hrefFor.CheckoutPage() &&
    pathname !== hrefFor.ShoppingBagPage();

  useEffect(() => {
    if (showModal) {
      tc.track('Welcome Email Modal Viewed', {
        addToTrackingEvent: {
          pagetype,
          label: userid
        }
      });
    }
  }, [pagetype, showModal, userid]);

  const [render, setRender] = useState(false);

  useEffect(() => {
    if (!isbot(navigator.userAgent)) {
      setRender(true);
    }
  }, [setRender]);

  const askForPhoneNumber = cloudfrontViewerCountry === 'US' && showPhoneNumberInput;
  const phoneText = askForPhoneNumber ? ' and phone.' : '.';

  return render ? (
    <ModalOverlay
      onClose={() => {
        setPreference('showEmailSignup', false);
        trackModalDismissed();
      }}
      show={showModal}
    >
      <div className="EmailSignup">
        <Clickable
          className="EmailSignup__close"
          aria-label="Close"
          onClick={() => {
            setPreference('showEmailSignup', false);
            trackModalDismissed();
          }}
        >
          <ExitIcon />
        </Clickable>
        <Stack className="EmailSignup__content" alignItems="center" space={4}>
          {!isSubscribed && (
            <Text className="EmailSignup__title" family="serif" treatment="h3">
              Get On The List!
            </Text>
          )}
          {isSubscribed && (
            <Text className="EmailSignup__title" family="serif" treatment="h3">
              You're on the list.
            </Text>
          )}

          {!isSubscribed && (
            <Stack alignItems="center" space={2}>
              <Text treatment="h6">Love what you're seeing?</Text>
              <Text treatment="h6">
                Enjoy <span className="EmailSignup__discount">10% off*</span> your first purchase
                and be the first to receive updates about what’s new and next in fashion, sent
                straight to your inbox{phoneText}
              </Text>
            </Stack>
          )}
          {isSubscribed && (
            <>
              {isExistingSubscription && (
                <div className="EmailSignup__status">
                  Looks like you're already on our newsletter list,{' '}
                  <Link
                    className="EmailSignup__manage-subscriptions-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    to={`${hrefFor.UnsubscribePage()}?email_hash=${sha('sha256')
                      .update(email)
                      .digest('hex')}`}
                  >
                    click here
                  </Link>{' '}
                  to manage your subscriptions.
                </div>
              )}
              {!isExistingSubscription && (
                <Text treatment="h6">
                  Now check your email—your <span className="EmailSignup__discount">10% off</span>{' '}
                  is waiting for you.
                </Text>
              )}
            </>
          )}

          {!isSubscribed && (
            <form className="EmailSignup__form" onSubmit={handleSubmit}>
              <Input
                className="EmailSignup__input"
                label="E-Mail"
                name="email"
                placeholder="E-mail Address"
                type="email"
                error={Boolean(error)}
                required
                value={email}
                onChange={setEmail}
              />
              {error && (
                <div
                  className={classNames('EmailSignup__status', {
                    'EmailSignup__status--error': error
                  })}
                >
                  {mailValidationError || 'Unable to subscribe you at this time.'}
                </div>
              )}
              {askForPhoneNumber && (
                <Input
                  className="EmailSignup__input"
                  label="SMS (Optional)"
                  name="phone"
                  placeholder="Phone Number"
                  type="phone"
                  error={Boolean(error)}
                  required
                />
              )}
              <Button type="submit" role="button">
                SIGN UP
              </Button>
            </form>
          )}

          {isSubscribed && (
            <Button
              className="EmailSignup__action"
              onClick={() => setPreference('showEmailSignup', false)}
              role="button"
            >
              CONTINUE SHOPPING
            </Button>
          )}

          <Stack space={2} className="EmailSignup__footer">
            <Text treatment="body2" color="cement">
              To opt out,{' '}
              <Link
                className="EmailSignup__link"
                to={hrefFor.UnsubscribePage()}
                rel="noopener noreferrer"
                target="_blank"
              >
                unsubscribe here
              </Link>{' '}
              or in our emails.
            </Text>
            {!isSubscribed && (
              <>
                <Text treatment="body2" color="cement">
                  *Terms and Conditions Apply
                </Text>
                {askForPhoneNumber && (
                  <Text treatment="body2" color="cement">
                    *By submitting this form, you agree to receive recurring automated promotional
                    and personalized marketing text messages (e.g. cart reminders) from Moda
                    Operandi at the cell number used when signing up. Consent is not a condition of
                    any purchase. Reply HELP for help and STOP to cancel. Msg frequency varies. Msg
                    &amp; data rates may apply.{' '}
                  </Text>
                )}
                <Text treatment="body2" color="cement">
                  View{' '}
                  <ControlLink
                    to={hrefFor.TermsAndConditionsPage()}
                    className="EmailSignup__footer-link"
                    underlined
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Text treatment="body2" color="cement">
                      Terms
                    </Text>
                  </ControlLink>{' '}
                  &amp;{' '}
                  <ControlLink
                    to={hrefFor.PrivacyPolicyPage()}
                    className="EmailSignup__footer-link"
                    underlined
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Text treatment="body2" color="cement">
                      Privacy Policy
                    </Text>
                  </ControlLink>
                </Text>
              </>
            )}
          </Stack>
        </Stack>
      </div>
    </ModalOverlay>
  ) : null;
};
